import { ChevronDown, ChevronRight } from 'lucide-react';
import { getConnectorPanelBorder, getConnectorStatusConfig, type LiveConnector } from './ConnectorCard';

/**
 * Collapsed form of a gun: number, status, and whatever distinguishes it from
 * its siblings. The expanded form is ConnectorCard.
 *
 * A full card is 491px against a 380px scroll window, so stacking them gave a
 * station with six guns eight screens of scrolling and no way to see two guns at
 * once. Rows are ~56px, so the whole station fits in view and detail is opt-in.
 */

interface ConnectorRowProps {
  connector: LiveConnector;
  expanded: boolean;
  onToggle: () => void;
  /**
   * Owning charge point's connectivity. When it is `false` the gun's status is
   * whatever was last reported before contact was lost, so it is not shown at
   * all rather than shown stale — the charger header already says Offline.
   */
  isOnline?: boolean | undefined;
  /**
   * Power and connector type, shown only when the guns on this charge point
   * differ — otherwise the charger header already says it for all of them.
   */
  showSpecs?: boolean;
}

export function ConnectorRow({
  connector,
  expanded,
  onToggle,
  showSpecs = false,
  isOnline,
}: ConnectorRowProps) {
  const statusConfig = getConnectorStatusConfig(connector.status, connector.rawStatus);
  const offline = isOnline === false;
  // A gun an operator disabled can still report 'available'. Saying so would be as
  // misleading as showing a stale status for an unreachable charger, so it is
  // labelled for what it is — and unlike offline, it is worth saying out loud.
  const disabled = connector.active === false;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  // Expanded, this row is the head of one panel rather than a card of its own:
  // the bottom corners square off so the body continues the shape, the bottom
  // border goes so no seam crosses the middle, and the edge colour matches the
  // body's rather than staying neutral grey.
  const shape = expanded
    ? `rounded-t-xl border-b-0 ${getConnectorPanelBorder(connector, isOnline)}`
    : 'rounded-xl border-gray-100 dark:border-gray-700';

  return (
    <button
      onClick={onToggle}
      aria-expanded={expanded}
      className={`w-full flex items-center gap-3 px-3 py-3 bg-gray-50 dark:bg-gray-800 border hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left ${shape}`}
    >
      <span
        className={`w-2 h-2 rounded-full flex-shrink-0 ${
          disabled || offline ? 'bg-gray-300 dark:bg-gray-600' : statusConfig.dotColor
        }`}
      ></span>

      <span className="font-semibold text-gray-900 dark:text-white flex-shrink-0">
        Gun {connector.gunNumber}
      </span>

      {showSpecs && (
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {connector.power} · {connector.type}
        </span>
      )}

      {disabled ? (
        <span className="ml-auto text-xs font-semibold text-gray-500 dark:text-gray-400 flex-shrink-0">
          Out of service
        </span>
      ) : (
        !offline && (
          <span className={`ml-auto text-xs font-semibold ${statusConfig.text} flex-shrink-0`}>
            {statusConfig.label}
          </span>
        )
      )}

      <Chevron
        className={`w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 ${
          offline && !disabled ? 'ml-auto' : ''
        }`}
      />
    </button>
  );
}
